import { AssigneeActions, IAssignee, IAssigneesStoreState } from "@gtmhub/assignees";
import { GtmhubController } from "@gtmhub/core";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { IdMap } from "@gtmhub/util";
import { IComponentOptions, IOnChangesObjectOf } from "angular";
import { AvatarSize } from "../circle-avatar/circle-avatar.component";

interface IAvatarsBindings {
  ids: string[];
  size: AvatarSize;
  alignRight: boolean;
  tooltipPlacement: string;
  previewNumber: number;
}

interface IAvatarsComponentRedux {
  assignees: IdMap<IAssignee>;
  assigneesFetched: boolean;
  assigneesError: IUIError;
}

export const splitArrayIntoTwo = <T>(
  array: T[] = [],
  splitIndex: number
): {
  firstPart: T[];
  secondPart: T[];
} => {
  if (array.length > splitIndex) {
    const firstPart = array.slice(0, splitIndex - 1);
    const secondPart = array.slice(splitIndex - 1);

    return { firstPart, secondPart };
  } else {
    return { firstPart: array, secondPart: [] };
  }
};

const bindStateToCtrl = (state: IAssigneesStoreState) => {
  const { map: assignees, isFetched: assigneesFetched, error: assigneesError } = state.assignees;
  return {
    assignees,
    assigneesFetched,
    assigneesError,
  };
};

/**
 * @example <avatars ids="goal.ownerIds" size="s-20" align-right="true"></avatars>
 * @param ids string[]
 * @param size "s-20" | "s-24" | "s-26"
 * @param alignRight boolean
 * @param tooltipPlacement "top" | "bottom" | "left" | "right"
 * @param previewNumber number
 */
export interface AvatarsCtrl extends IAvatarsBindings, IAvatarsComponentRedux {}
export class AvatarsCtrl extends GtmhubController {
  additionalIds: string[];
  previewIds: string[];

  static $inject = ["$ngRedux", "AssigneeActions", "$scope", "UIErrorHandlingService"];
  constructor($ngRedux: INgRedux, assigneeActions: AssigneeActions, private $scope: IExtendedRootScopeService, private uiErrorHandlingService: UIErrorHandlingService) {
    super();

    this.onDestroy($ngRedux.connect(bindStateToCtrl)(this));
    $ngRedux.dispatch(assigneeActions.getAssignees());
  }

  $onInit(): void {
    this.$scope
      .watchUntilAndRejectOnError(
        () => this.assigneesFetched,
        () => this.assigneesError
      )
      .then(
        () => this.initializeData(),
        () => this.uiErrorHandlingService.handleModal(this.assigneesError)
      );
  }

  $onChanges(onChangesObj: IOnChangesObjectOf<IAvatarsBindings>): void {
    if (
      Array.isArray(onChangesObj.ids.currentValue) &&
      Array.isArray(onChangesObj.ids.previousValue) &&
      onChangesObj.ids.currentValue.toString() !== onChangesObj.ids.previousValue.toString()
    ) {
      this.initializeData();
    }
  }

  private initializeData() {
    if (!this.previewNumber) {
      this.previewNumber = 5;
    }

    if (!this.ids) {
      this.ids = [];
    }

    const { firstPart, secondPart } = splitArrayIntoTwo(this.ids, this.previewNumber);
    this.previewIds = firstPart;
    this.additionalIds = secondPart;
  }
}

export const AvatarsComponent: IComponentOptions = {
  controller: AvatarsCtrl,
  templateUrl: require("./avatars.jade"),
  transclude: true,
  bindings: {
    ids: "<",
    size: "@",
    alignRight: "<",
    tooltipPlacement: "@",
    previewNumber: "<",
  },
};
